import {
  AlgoliaSearchProduct,
  DeprecatedAlgoliaSearchProduct,
} from "../../../src/types/Algolia";
import { ProductData } from "../types/schema.types";
import { OfferItemCondition } from "schema-dts";
import { getBrandUrl } from "./helpers";

/**
 * Configuration options for the Algolia product transformer
 */
export interface AlgoliaTransformerConfig {
  /** Base URL for generating absolute URLs */
  baseUrl?: string;
  /** Default currency if not provided in product data */
  defaultCurrency?: string;
  /** Default seller name */
  sellerName?: string;
  /** Gender context for URL generation */
  gender?: string;
  /** Locale for URL generation */
  locale?: string;
}

/**
 * Transforms an AlgoliaSearchProduct to ProductData format
 * @param algoliaProduct - The Algolia product data
 * @param config - Configuration options for the transformation
 * @returns ProductData object ready for schema generation
 */
export function transformAlgoliaProductToProductData(
  product: AlgoliaSearchProduct | DeprecatedAlgoliaSearchProduct,
  config: AlgoliaTransformerConfig = {}
): ProductData {
  const {
    baseUrl = "",
    defaultCurrency = "EUR",
    sellerName = "Otrium",
    gender = "women",
    locale = "en-gb",
  } = config;

  const algoliaProduct = product as AlgoliaSearchProduct;

  // Determine availability based on stock status
  const getAvailability = ():
    | "InStock"
    | "OutOfStock"
    | "LimitedAvailability" => {
    // For Algolia products, we assume they are in stock if they appear in search results
    // You might want to add stock checking logic here if available
    if (algoliaProduct.stock_amount !== undefined) {
      if (algoliaProduct.stock_amount === 0) {
        return "OutOfStock";
      }
      if (algoliaProduct.stock_amount < 10) {
        return "LimitedAvailability";
      }
    }
    return "InStock";
  };

  // Generate product URL
  const productUrl = baseUrl ? `${baseUrl}/product/${algoliaProduct.slug}` : "";

  // Get category information
  const getCategoryPath = (): string => {
    if (algoliaProduct.categories && algoliaProduct.categories.length > 0) {
      return algoliaProduct.categories.map((cat) => cat.name).join(" > ");
    }
    return "";
  };

  // Get color information
  const getColor = (): string | undefined => {
    if (
      algoliaProduct.color &&
      typeof algoliaProduct.color === "object" &&
      "value" in algoliaProduct.color
    ) {
      return algoliaProduct.color.value as string;
    }
    return undefined;
  };

  const productData: ProductData = {
    name: algoliaProduct.name,
    description: "",
    images: algoliaProduct.image ? [algoliaProduct.image] : [],
    sku: algoliaProduct.ban_code,
    brand: algoliaProduct.brand_name || algoliaProduct.brand?.name || "",
    brandUrl: getBrandUrl(algoliaProduct.brand.slug, locale, gender),
    price: algoliaProduct.price,
    regularPrice: algoliaProduct.regular_price,
    currency: algoliaProduct.currency?.code || defaultCurrency,
    availability: getAvailability(),
    url: productUrl,
    gtin: undefined, // Not available in Algolia data
    mpn: undefined, // Not available in Algolia data
    color: getColor(),
    material: undefined, // Not available in Algolia data
    size: undefined,
    rating: undefined, // Not available in Algolia data
    condition: "https://schema.org/NewCondition" as OfferItemCondition,
    category: getCategoryPath(),
    careInstructions: undefined, // Not available in Algolia data
    variations: undefined,
    labels: undefined,
    status: algoliaProduct.is_deleted ? "deleted" : "active",
    dateCreated: algoliaProduct.publish_date_human,
    dateModified: undefined, // Not available in Algolia data
    gate: algoliaProduct.gate?.toString(),
    shopType: algoliaProduct.genders || [gender],
    kidsGender: undefined, // Could be derived from genders if needed
    seller: sellerName,
    sellerUrl: baseUrl,
    buyActionUrl: productUrl,
  };

  return productData;
}

/**
 * Transforms an array of AlgoliaSearchProduct to ProductData format
 * @param algoliaProducts - Array of Algolia product data
 * @param config - Configuration options for the transformation
 * @returns Array of ProductData objects
 */
export function transformAlgoliaProductsToProductData(
  algoliaProducts: AlgoliaSearchProduct[],
  config: AlgoliaTransformerConfig = {}
): ProductData[] {
  return algoliaProducts.map((product) =>
    transformAlgoliaProductToProductData(product, config)
  );
}
